import { useState } from 'react';

import { AGENDA_MAX_ITEMS } from './agendaLimits';

import type { AgendaDraft } from './agendaDraft';
import type { AgendaItemView } from './agendaViewModel';

interface TopicUpdate {
    itemId: string;
    title: string;
    durationMinutes: number;
}

/** The agenda's topics: added, renamed, removed, reordered, and folded open or closed. */
export const useAgendaTopics = ({ items, commit, editable }: AgendaDraft) => {
    const [expandedIds, setExpandedIds] = useState<string[]>([]);
    const [isAdding, setIsAdding] = useState(false);
    const canAdd = items.length < AGENDA_MAX_ITEMS;

    const confirmAdd = (title: string, durationMinutes: number) => {
        if (!canAdd) {
            return;
        }

        const topic: AgendaItemView = {
            id: crypto.randomUUID(),
            title,
            durationMinutes,
            order: items.length,
            hasHandout: false,
            isAiGenerated: false,
            discussionPoints: [],
        };
        commit([...items, topic]);
        setIsAdding(false);
    };

    const update = ({ itemId, title, durationMinutes }: TopicUpdate) =>
        commit(
            items.map((item) => (item.id === itemId ? { ...item, title, durationMinutes } : item)),
        );

    const remove = (itemId: string) =>
        commit(
            items
                .filter((item) => item.id !== itemId)
                .map((item, index) => ({ ...item, order: index })),
        );

    return {
        /** False once the agenda holds as many topics as the backend validator accepts. */
        canAdd,
        isAdding,
        isExpanded: (itemId: string) => expandedIds.includes(itemId),
        toggleExpanded: (itemId: string) =>
            setExpandedIds((current) =>
                current.includes(itemId)
                    ? current.filter((id) => id !== itemId)
                    : [...current, itemId],
            ),
        setIsAdding: editable(setIsAdding),
        confirmAdd: editable(confirmAdd),
        update: editable(update),
        remove: editable(remove),
        reorder: editable(commit),
    };
};
